import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { notificationService } from './notificationService';

// Firebase configuration - matches your service worker config
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDUxFxAy1zhP8ZmXhyPK8LJjGVt0oA8z2E",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "e---commerce-4487e.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "e---commerce-4487e",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "e---commerce-4487e.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "784514136985",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:784514136985:web:38bf3e52043cfeed08f108",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-EVFHD3DGKF"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
let messaging: any = null;

// Check if we're in a browser environment and service workers are supported
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  try {
    messaging = getMessaging(app);
  } catch (error) {
    console.warn('Firebase messaging not available:', error);
  }
}

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: Record<string, any>;
}

export const firebaseService = {
  // Initialize Firebase messaging and request permission
  initialize: async (): Promise<string | null> => {
    console.log('🔔 Initializing Firebase messaging...');
    
    if (!messaging) {
      console.error('🔔 Firebase messaging not available - check configuration');
      return null;
    }

    try {
      // Check if service worker is registered
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          const swRegistered = registrations.some(reg => 
            reg.scope.includes('firebase-messaging-sw.js') || 
            reg.active?.scriptURL.includes('firebase-messaging-sw.js')
          );
          
          if (!swRegistered) {
            console.log('🔔 Registering Firebase service worker...');
            await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.log('🔔 Service worker registered successfully');
          } else {
            console.log('🔔 Service worker already registered');
          }
        } catch (swError) {
          console.error('🔔 Service worker registration failed:', swError);
        }
      }

      // Request notification permission
      console.log('🔔 Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('🔔 Permission result:', permission);
      
      if (permission === 'granted') {
        console.log('🔔 Notification permission granted, getting FCM token...');
        
        // Get FCM token - try with and without VAPID key
        let token = null;
        const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
        
        try {
          if (vapidKey && vapidKey !== 'your-vapid-key') {
            console.log('🔔 Using VAPID key for token generation');
            token = await getToken(messaging, { vapidKey });
          } else {
            console.log('🔔 No VAPID key provided, trying without it');
            token = await getToken(messaging);
          }
        } catch (tokenError) {
          console.error('🔔 Error getting FCM token:', tokenError);
          
          // Try without VAPID key as fallback
          if (vapidKey) {
            console.log('🔔 Retrying without VAPID key...');
            try {
              token = await getToken(messaging);
            } catch (fallbackError) {
              console.error('🔔 Fallback token generation also failed:', fallbackError);
            }
          }
        }
        
        if (token) {
          console.log('🔔 FCM Token received:', token.substring(0, 20) + '...');
          
          // Update token on server
          try {
            await notificationService.updateFCMToken(token);
            console.log('🔔 FCM token updated on server successfully');
          } catch (error) {
            console.error('🔔 Failed to update FCM token on server:', error);
          }
          
          return token;
        } else {
          console.warn('🔔 No FCM token received - check Firebase configuration');
          return null;
        }
      } else {
        console.warn('🔔 Notification permission denied or not granted:', permission);
        return null;
      }
    } catch (error) {
      console.error('🔔 Error initializing Firebase messaging:', error);
      return null;
    }
  },

  // Listen for foreground messages
  onMessage: (callback: (payload: MessagePayload) => void): (() => void) | null => {
    if (!messaging) {
      console.warn('Firebase messaging not available');
      return null;
    }

    try {
      const unsubscribe = onMessage(messaging, (payload) => {
        console.log('🔔 Foreground message received:', payload);
        
        // Show browser notification if not already shown by Firebase
        if (payload.notification) {
          firebaseService.showNotification({
            title: payload.notification.title || 'New Notification',
            body: payload.notification.body || '',
            icon: payload.notification.icon,
            data: payload.data
          });
        }
        
        callback(payload);
      });

      return unsubscribe;
    } catch (error) {
      console.error('🔔 Error setting up message listener:', error);
      return null;
    }
  },

  // Show browser notification
  showNotification: (payload: NotificationPayload): void => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(payload.title, {
        body: payload.body,
        icon: payload.icon || '/favicon.ico',
        badge: payload.badge || '/favicon.ico',
        data: payload.data,
        requireInteraction: true,
        actions: [
          {
            action: 'view',
            title: 'View'
          },
          {
            action: 'dismiss',
            title: 'Dismiss'
          }
        ]
      });

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        
        // Handle navigation based on notification data
        if (payload.data?.action) {
          firebaseService.handleNotificationAction(payload.data.action, payload.data);
        }
        
        notification.close();
      };

      // Auto close after 10 seconds
      setTimeout(() => {
        notification.close();
      }, 10000);
    }
  },

  // Handle notification actions
  handleNotificationAction: (action: string, data?: Record<string, any>): void => {
    console.log('🔔 Handling notification action:', action, data);
    
    switch (action) {
      case 'open_bookings':
        window.location.href = '/bookings';
        break;
      case 'open_orders':
        window.location.href = '/trips';
        break;
      case 'open_profile':
        window.location.href = '/settings';
        break;
      case 'open_notifications':
        window.location.href = '/notifications';
        break;
      case 'open_updates':
        // Handle system updates
        break;
      default:
        // Default action - just focus the window
        window.focus();
        break;
    }
  },

  // Get current FCM token
  getCurrentToken: async (): Promise<string | null> => {
    if (!messaging) {
      return null;
    }

    try {
      const token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY || 'your-vapid-key'
      });
      return token;
    } catch (error) {
      console.error('🔔 Error getting FCM token:', error);
      return null;
    }
  },

  // Check if notifications are supported
  isSupported: (): boolean => {
    return !!(
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      messaging
    );
  },

  // Get notification permission status
  getPermissionStatus: (): NotificationPermission | null => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return null;
  }
};
